import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

const NEW_STORAGE_KEY = 'refreshToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);

      const refreshToken = sessionStorage.getItem(NEW_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken) && refreshToken) {
        setSession(accessToken, refreshToken);

        const response = await axios.get(endpoints.auth.me);

        const { user } = response.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...user,
              accessToken,
            },
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const username = email;

    let deviceId = '';
    try {
      const deviceResponse = await axios.get('http://127.0.0.1:52731/device-id');
      deviceId = deviceResponse?.data?.deviceId || '';
    } catch {
      // The optional local device service must never prevent authentication.
    }

    const data = {
      username,
      password,
      device_id: deviceId,
    };

    const response = await axios.post(endpoints.auth.login, data);

    const { accessToken, refreshToken, user } = response.data;

    setSession(accessToken, refreshToken);

    let fullUser = user;
    try {
      const meResponse = await axios.get(endpoints.auth.me);
      if (meResponse?.data?.user) {
        fullUser = { ...user, ...meResponse.data.user };
      }
    } catch {
      // Fallback to login response user data
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...fullUser,
          accessToken,
        },
      },
    });
  }, []);

   // LOGIN
  const thelogin = useCallback(async (email, password) => {
    const username = email;

    const data = {
      username,
      password,
    };

    const response = await axios.post(endpoints.auth.thelogin, data);

    const { accessToken, refreshToken, user } = response.data;

    sessionStorage.setItem('type', 'thelogin');
    setSession(accessToken, refreshToken);

    let fullUser = user;
    try {
      const meResponse = await axios.get(endpoints.auth.me);
      if (meResponse?.data?.user) {
        fullUser = { ...user, ...meResponse.data.user };
      }
    } catch {
      // Fallback to login response user data
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...fullUser,
          accessToken,
        },
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { accessToken, user } = response.data;

    sessionStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user: {
          ...user,
          accessToken,
        },
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    const refreshToken = sessionStorage.getItem(NEW_STORAGE_KEY);
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) {
        await axios.post(endpoints.auth.logout, { refreshToken });
      }
    } finally {
      setSession(null);
      dispatch({
        type: 'LOGOUT',
      });
    }
  }, []);

  // REFRESH USER
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(endpoints.auth.me);
      const { user } = response.data;
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            ...user,
            accessToken,
          },
        },
      });
      return user;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      thelogin,
      register,
      logout,
      refreshUser,
    }),
    [login, thelogin, logout, register, refreshUser, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
